// Forked from https://github.com/vbenjs/vue-vben-admin/tree/main/src/utils/http/axios
// The axios configuration can be changed according to the project,
// just change the file, other files can be left unchanged

import type { ApiErrorDetail, ApiErrorResult, RequestOptions } from '/#/axios'
import { ApiTextAreaError } from '/#/axios'
import type { NotificationOptions } from '/#/components/base/notification'
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform'
import type { AxiosResponse } from 'axios'

import axiosRetry from 'axios-retry'
import constant from 'lodash/constant'
import clone from 'lodash-es/clone'
import isArray from 'lodash-es/isArray'
import isObject from 'lodash-es/isObject'
import isString from 'lodash-es/isString'
import merge from 'lodash-es/merge'
import qs from 'qs'

import { CSRF_TOKEN_KEY, FAKE_API_KEYWORD } from '/@/constants/common'
import { ContentTypeEnum, HttpStatusCodeEnum, RequestEnum } from '/@/enums/httpEnum'
import { setObjToUrlParams } from '/@/utils'
import { VAxios } from '/@/utils/http/axios'
import { checkStatus } from '/@/utils/http/checkStatus'
import { checkAccountExpired, formatRequestDate } from '/@/utils/http/helper'

const settings = useSettings()

const { setAxiosProgress } = useAppStoreWithout()

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: Process request data.
   * If the data is not in the expected format, an error can be thrown directly
   */
  transformRequestHook: (res: AxiosResponse, options: RequestOptions) => {
    const { isTransformResponse, isReturnNativeResponse } = options
    // Whether to return native response headers
    // For example: use this property when you need to get response headers
    if (isReturnNativeResponse) {
      return res
    }
    // Return without any processing
    // For page code, it may be necessary to directly obtain code, data, and message information.
    if (!isTransformResponse) {
      return res.data
    }
    return res.data
  },

  // Process config before request
  // eslint-disable-next-line sonarjs/cognitive-complexity
  beforeRequestHook: (config, options) => {
    const { apiUrl, joinParamsToUrl, formatDate } = options
    let baseUrl = apiUrl

    if (config.url?.includes(FAKE_API_KEYWORD)) {
      // If the URL contains 'fake-api', the real API URL should not be used. Base url should communicate with msw.
      baseUrl = FAKE_API_KEYWORD
    }

    // make full url
    // if the url already contains apiUrl there is no need here
    if (baseUrl && isString(baseUrl) && !config.url?.includes(baseUrl)) {
      config.url = `${baseUrl}${config.url}`
    }
    // get request params
    const params = config.params || {}
    // get request data
    const data = config.data || false

    // eslint-disable-next-line no-unused-expressions
    formatDate && data && !isString(data) && formatRequestDate(data)

    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // Add a timestamp parameter to the request to avoid getting data from the cache.
        config.params = Object.assign(params || {}, {})
      } else {
        // Compatible with restful style
        config.url += params
        config.params = undefined
      }

      // Creates a retry request mechanism for GET Requests
      if (options.retryRequest?.isOpenRetry) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const instance = defHttp.getAxios()
        axiosRetry(instance, {
          retries: options.retryRequest?.count || 0,
          retryDelay: constant(options.retryRequest!.waitTime),
          shouldResetTimeout: true,
        })
      }
    } else if (!isString(params)) {
      // eslint-disable-next-line no-unused-expressions
      formatDate && formatRequestDate(params)
      if (
        Reflect.has(config, 'data') &&
        config.data &&
        (Object.keys(config.data).length > 0 || config.data instanceof FormData)
      ) {
        config.data = data
        config.params = params
      } else {
        // Non-GET requests treat params as data if no data is provided
        config.data = params
        config.params = undefined
      }
      if (joinParamsToUrl) {
        config.url = setObjToUrlParams(
          config.url as string,
          Object.assign({}, config.params, config.data),
        )
      }
    } else {
      // Compatible with restful style
      config.url += params
      config.params = undefined
    }
    return config
  },

  /**
   * @description: Request interceptor processing
   */
  requestInterceptors: (config, options) => {
    // Process config before request
    const token = ''
    if ((config as Recordable)?.requestOptions?.withToken !== false) {
      // jwt token
      ;(config as Recordable).headers.Authorization = options.authenticationScheme
        ? `${options.authenticationScheme} ${token}`
        : token
    }

    // send csrf token header
    const accountStore = useAccountStoreWithout()
    const csrfToken = accountStore.csrfToken
    if (csrfToken && config.method !== RequestEnum.GET) {
      ;(config as Recordable).headers[CSRF_TOKEN_KEY] = csrfToken
    }
    return config
  },

  /**
   * @description: Response interceptor processing
   */
  responseInterceptors: (res: AxiosResponse) => {
    return res
  },

  /**
   * @description: response error handling
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  responseInterceptorsCatch: async (axiosInstance: AxiosResponse, error: any) => {
    const notify = useNotify()
    const { response, config } = error || {}

    const isExcluded = config?.requestOptions?.excludeCommonError ?? false
    const hideNotify = config?.requestOptions?.hideNotify ?? false

    const msg: string = response?.data?.detail ?? ''
    const statusCode = response?.data?.status_code
    const err: string = error?.toString?.() ?? ''

    const errorResult: ApiErrorResult | undefined = response?.data
    const errors: ApiErrorDetail[] | ApiTextAreaError | undefined = errorResult?.errors

    let errMessage = ''

    try {
      if (err?.includes('Network Error')) {
        errMessage =
          'Network connection error. Please check your internet connection and try again.'
        notify.error(errMessage)
      }

      // Handle timeout errors
      if (err?.includes('timeout')) {
        errMessage = 'Request timed out. Please try again later.'
        notify.error(errMessage)
      }

      if (checkAccountExpired(response)) {
        const accountStore = useAccountStoreWithout()
        await accountStore.logout(false)
      }

      // Does not throw any notify when an error occurs.
      if (hideNotify) return Promise.reject(error)

      if (response?.data instanceof Blob && isExcluded) {
        const blobResponse = await new Response(response.data as BodyInit).json()

        notify.error(blobResponse.errors[0].detail)
        return Promise.reject(error)
      }

      if (isArray(errors) && errors?.length > 0) {
        // If the backend throws an error after submitting the form, show this notify
        // TODO Refactor this condition @oğuzhan
        const accountStore = useAccountStoreWithout()

        if (statusCode === HttpStatusCodeEnum.HTTP_400_BAD_REQUEST && !isExcluded) {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          notify.open(getErrorMessage(statusCode) as NotificationOptions)
        } else if (accountStore.isLoggedIn) {
          errors.forEach((error: any) => notify.error(error.detail))
        }
        return Promise.reject(error)
      }

      if (isObject(errors)) {
        if (statusCode === HttpStatusCodeEnum.HTTP_400_BAD_REQUEST && !isExcluded) {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          notify.open(getErrorMessage(statusCode) as NotificationOptions)
        } else if ('errors' in errors) {
          errors.errors.forEach((error: any) => notify.error(error.detail))
        }
        return Promise.reject(error)
      }
    } catch (error) {
      throw new Error(error as string)
    }

    checkStatus(error?.response?.status, msg)
    return Promise.reject(error)
  },
}

function getErrorMessage(statusCode: number) {
  return {
    [HttpStatusCodeEnum.HTTP_400_BAD_REQUEST]: {
      title: 'Oops! Something went wrong.',
      message: "Please check the form's field inputs and try again.",
      type: 'error',
    },
  }[statusCode]
}

function createAxios(opt?: Partial<CreateAxiosOptions>) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return new VAxios(
    merge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        // We use session authentication. So it's empty.
        authenticationScheme: '',
        timeout: 10 * 1000,
        // base interface address
        apiUrl: settings.apiUrl,

        headers: { 'Content-Type': ContentTypeEnum.JSON, 'X-User-Timezone': timezone },
        // If it is form-data format
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // Data processing method
        transform: clone(transform),
        // send cookies (because we use session authentication.)
        withCredentials: true,
        // Use comma as separator for arrays.
        paramsSerializer: {
          serialize: (params: Recordable) =>
            qs.stringify(params, { arrayFormat: 'comma' }),
        },
        // Configuration items, the following options can be
        // overridden in independent interface requests
        requestOptions: {
          // Whether to return native response headers For example:
          // use this property when you need to get response headers
          isReturnNativeResponse: false,
          // The returned data needs to be processed
          isTransformResponse: true,
          // Add parameters to the url when making a post request
          joinParamsToUrl: false,
          // Format submit parameter time
          formatDate: true,
          // message type
          errorMessageMode: 'message',
          // interface address
          apiUrl: settings.apiUrl,
          //  Whether to add timestamp
          joinTime: true,
          // Ignore duplicate requests
          ignoreCancelToken: true,
          // Whether to carry the token
          withToken: true,
          retryRequest: {
            isOpenRetry: true,
            count: 5,
            waitTime: 5000,
          },
        },

        onUploadProgress: (progressEvent: ProgressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )

          // If the progress is 100, it means that the upload is complete, and the progress bar is hidden
          if (percentCompleted === 100) setAxiosProgress(null)
          else setAxiosProgress(percentCompleted)
        },
      },
      opt || {},
    ),
  )
}

export const defHttp = createAxios()
